import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { useBrandsControllerGetClientBrandProfile } from '@/apis/backend/generated/brands/brands';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';

import { paths } from 'src/routes/paths';

import { Iconify } from 'src/components/iconify';
import { BrandPageImportsEditor } from 'src/components/settings/BrandPageImportsEditor';

export const Route = createFileRoute(
  '/_authed/dashboard/o/$orgId/settings/client/brand/$clientId/page-imports',
)({
  component: BrandPageImportsPage,
});

function BrandPageImportsPage() {
  const navigate = useNavigate();
  const { orgId, clientId } = Route.useParams();

  const { data, isLoading } = useBrandsControllerGetClientBrandProfile(orgId, clientId, {
    query: { enabled: !!orgId && !!clientId },
  });
  const brandProfile = data?.data;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Button
            size="small"
            startIcon={<Iconify icon="eva:arrow-back-fill" width={16} />}
            onClick={() => navigate({ to: paths.dashboard.orgSettings(orgId).client.brandDetail(clientId) })}
            sx={{ textTransform: 'none', mb: 1 }}
          >
            Brand profile
          </Button>
          <Typography variant="h4" gutterBottom>
            Import from a page
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Point at a specific page and pull what is on it into this brand. Images land in your
            media library and products land in your catalog. Brand colors and fonts are only ever
            proposed for your review under Brand Knowledge, never applied automatically.
          </Typography>
        </Box>
      </Stack>

      {isLoading ? (
        <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : !brandProfile ? (
        <Alert
          severity="info"
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() =>
                navigate({ to: paths.dashboard.orgSettings(orgId).client.brandDetail(clientId) })
              }
            >
              Go to brand profile
            </Button>
          }
        >
          This client has no brand profile yet. Create one before importing pages.
        </Alert>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <BrandPageImportsEditor brandProfileId={brandProfile.id} />
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
